import {
  Combobox,
  Highlight,
  Spinner,
  useCombobox,
  useComboboxContext,
  useFilter,
  useListCollection,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef } from 'react';

import { HStack, Typography } from 'shared';

export type SearchableSelectItem = {
  label: string;
  value: string;
};

type SearchableSelectProps<T extends SearchableSelectItem> = {
  items: T[];
  selectedValue?: string;
  onSelect: (item: T) => void;
  placeholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  isLoading?: boolean;
  itemToString?: (item: T) => string;
  itemToValue?: (item: T) => string;
  dataTestid?: string;
  size?: Combobox.RootProps['size'];
};

export const SearchableSelect = <T extends SearchableSelectItem>({
  items,
  selectedValue,
  onSelect,
  placeholder = 'Search...',
  emptyMessage = 'No items found',
  loadingMessage = 'Loading...',
  isLoading = false,
  itemToString = (item) => item.label,
  itemToValue = (item) => item.value,
  dataTestid,
  ...rest
}: SearchableSelectProps<T>) => {
  const { contains } = useFilter({ sensitivity: 'base' });

  const { collection, filter, set } = useListCollection({
    initialItems: [] as T[],
    itemToString,
    itemToValue,
    filter: contains,
  });

  useEffect(() => {
    if (items.length > 0) {
      set(items);
    }
  }, [items, set]);

  const handleValueChange = (details: { value: string[]; items: T[] }) => {
    const selectedItem = details.items[0];
    if (selectedItem) {
      onSelect(selectedItem);
    }
  };

  const handleInputValueChange = (details: { inputValue: string }) => {
    filter(details.inputValue);
  };

  const value = useMemo(
    () => (selectedValue ? [selectedValue] : []),
    [selectedValue]
  );

  const combobox = useCombobox({
    collection,
    value,
    onValueChange: handleValueChange,
    onInputValueChange: handleInputValueChange,
    openOnClick: true,
  });

  // Rehydrate the selected value after collection is populated
  const hydrated = useRef(false);
  useEffect(() => {
    if (value.length > 0 && collection.size > 0 && !hydrated.current) {
      combobox.syncSelectedItems();
      hydrated.current = true;
    }
  }, [value, collection.size, combobox]);

  if (isLoading) {
    return (
      <HStack>
        <Typography>{loadingMessage}</Typography>
        <Spinner size="md" />
      </HStack>
    );
  }

  return (
    <Combobox.RootProvider
      value={combobox}
      positioning={{ sameWidth: true }}
      {...rest}
    >
      <Combobox.Control data-testid={dataTestid}>
        <Combobox.Input placeholder={placeholder} />
        <Combobox.IndicatorGroup>
          <Combobox.ClearTrigger />
          <Combobox.Trigger />
        </Combobox.IndicatorGroup>
      </Combobox.Control>

      <Combobox.Positioner>
        <Combobox.Content maxH="20rem">
          <Combobox.Empty>{emptyMessage}</Combobox.Empty>
          {collection.items.map((item: T) => (
            <HighlightedItem
              key={itemToValue(item)}
              item={item}
              itemToString={itemToString}
            />
          ))}
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.RootProvider>
  );
};

type HighlightedItemProps<T extends SearchableSelectItem> = {
  item: T;
  itemToString: (item: T) => string;
};

const HighlightedItem = <T extends SearchableSelectItem>({
  item,
  itemToString,
}: HighlightedItemProps<T>) => {
  const combobox = useComboboxContext();

  return (
    <Combobox.Item item={item}>
      <Combobox.ItemText>
        <Highlight
          ignoreCase
          query={combobox.inputValue}
          styles={{ bg: 'yellow.200', fontWeight: 'medium' }}
        >
          {itemToString(item)}
        </Highlight>
      </Combobox.ItemText>
      <Combobox.ItemIndicator />
    </Combobox.Item>
  );
};
